import { clearAllInsetProperties, getDefaultInsetValues } from "./config.ts";
import type { InsetProperty, OffsetOrigin, SliderDirection } from "./types.ts";
import { offsetOrigins } from "./types.ts";

const allowedOffsetOriginsBySliderDirection: Record<SliderDirection, ReadonlySet<OffsetOrigin>> = {
  "from-left": new Set<OffsetOrigin>(["topLeft", "centerLeft", "bottomLeft"]),
  "from-right": new Set<OffsetOrigin>(["topRight", "centerRight", "bottomRight"]),
  "from-top": new Set<OffsetOrigin>(["topLeft", "topCenter", "topRight"]),
  "from-bottom": new Set<OffsetOrigin>(["bottomLeft", "bottomCenter", "bottomRight"]),
};

/** Returns the inset properties along the slider's axis that should be disabled. */
export const getSliderConflictingInsetProperties = (direction: SliderDirection): ReadonlySet<InsetProperty> =>
  direction === "from-top" || direction === "from-bottom" ? new Set(["top", "bottom"]) : new Set(["left", "right"]);

export const getDisabledOffsetOriginsForSliderDirection = (direction: SliderDirection): ReadonlySet<OffsetOrigin> => {
  const allowed = allowedOffsetOriginsBySliderDirection[direction];
  return new Set(offsetOrigins.filter((origin) => !allowed.has(origin)));
};

/**
 * Snaps an offset origin to the nearest allowed one for a given slider direction.
 * Preserves the row for horizontal sliders, column for vertical sliders.
 */
export const getNearestAllowedOffsetOrigin = (
  currentOffsetOrigin: OffsetOrigin,
  direction: SliderDirection,
): OffsetOrigin => {
  const allowed = allowedOffsetOriginsBySliderDirection[direction];
  if (allowed.has(currentOffsetOrigin)) return currentOffsetOrigin;

  const rowOf = (ap: OffsetOrigin): string => {
    if (ap.startsWith("top")) return "top";
    if (ap.startsWith("bottom")) return "bottom";
    return "center";
  };

  const colOf = (ap: OffsetOrigin): string => {
    if (ap.endsWith("Left")) return "left";
    if (ap.endsWith("Right")) return "right";
    return "center";
  };

  const currentRow = rowOf(currentOffsetOrigin);
  const currentCol = colOf(currentOffsetOrigin);

  if (direction === "from-left" || direction === "from-right") {
    const sameRow = [...allowed].find((ap) => rowOf(ap) === currentRow);
    if (sameRow) return sameRow;
  }

  if (direction === "from-top" || direction === "from-bottom") {
    const sameCol = [...allowed].find((ap) => colOf(ap) === currentCol);
    if (sameCol) return sameCol;
  }

  return [...allowed][0];
};

/**
 * Returns the config patch needed to correct the offset origin for a new slider direction,
 * or undefined if no correction is needed.
 */
export const getOffsetOriginCorrectionForSliderDirection = (
  currentOffsetOrigin: OffsetOrigin | undefined,
  newDirection: SliderDirection,
) => {
  if (!currentOffsetOrigin) return undefined;
  const corrected = getNearestAllowedOffsetOrigin(currentOffsetOrigin, newDirection);
  if (corrected === currentOffsetOrigin) return undefined;
  return {
    cssProperties: { ...clearAllInsetProperties(), ...getDefaultInsetValues(corrected) },
    layoutSettings: { offsetOrigin: corrected },
  };
};
